import { useEffect, useState } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { useParams, useOutletContext, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import PropertyMap from '../../components/common/PropertyMap';
import {
  MapPin,
  ShieldCheck,
  Building2,
  Home,
  TreeDeciduous,
  Phone,
  Mail,
  ArrowLeft,
  ArrowRight,
  Bed,
  Bath,
  Users,
  Maximize,
  CheckCircle,
  MessageCircle,
  IndianRupee,
  Compass,
  Calendar,
  Layers,
  AlertTriangle,
  Share2,
  Printer,
  Navigation,
  Droplets,
  Zap,
  Scale,
  Landmark,
  FileCheck,
  Video,
  ExternalLink,
  Type as TypeIcon,
  Flag,
  Heart
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import ImageMosaic from '../../components/property/ImageMosaic';
import AmenitiesGrid from '../../components/property/AmenitiesGrid';
import PropertySidebar from '../../components/property/PropertySidebar';
import NeighborhoodInsights from '../../components/property/NeighborhoodInsights';
import { Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypeIcons = {
  apartment: Building2,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2,
  venture: Building2
};

const facingLabels = {
  east: 'East', west: 'West', north: 'North', south: 'South',
  north_east: 'North-East', north_west: 'North-West',
  south_east: 'South-East', south_west: 'South-West'
};

const furnishingLabels = {
  unfurnished: 'Unfurnished', semi_furnished: 'Semi-Furnished', fully_furnished: 'Fully Furnished'
};

const parkingLabels = {
  none: 'None', open: 'Open', covered: 'Covered', both: 'Open + Covered'
};

const waterLabels = {
  municipal: 'Municipal', borewell: 'Borewell', both: 'Both'
};

const ownershipLabels = {
  freehold: 'Freehold', leasehold: 'Leasehold',
  cooperative: 'Co-operative Society', power_of_attorney: 'Power of Attorney'
};

const landTypeLabels = {
  residential: 'Residential', commercial: 'Commercial',
  agricultural: 'Agricultural', industrial: 'Industrial'
};

// Helper to check if a video URL is embeddable (YouTube/Vimeo)
function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

// Detail row component
function DetailRow({ icon: Icon, label, value }) {
  if (!value && value !== 0 && value !== false) return null;
  const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-9 h-9 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-navy-600" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-navy-500">{label}</p>
        <p className="font-medium text-navy-900 text-sm truncate">{displayValue}</p>
      </div>
    </div>
  );
}

export default function AgencyPropertyDetail() {
  const { propertyId } = useParams();
  const location = useLocation();
  const { agency } = useOutletContext();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeMediaTab, setActiveMediaTab] = useState('photos');
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/property/${propertyId}`);
      setProperty(response.data.property);
    } catch (error) {
      console.error('Failed to fetch property:', error);
    } finally {
      setLoading(false);
    }
  };

  // Favorites & Reporting Logic
  const { isCustomer, getAuthHeader } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportForm, setReportForm] = useState({ reason: 'fraud', description: '' });

  useEffect(() => {
    if (isCustomer && property) {
      checkFavoriteStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer, property?.id]);

  const checkFavoriteStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/favorites`, { headers: getAuthHeader() });
      const favIds = res.data.properties.map(p => p.id);
      setIsFavorite(favIds.includes(property.id));
    } catch (error) {
      console.error('Failed to check favorites:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isCustomer) {
      toast.error('Please login as a customer to save favorites');
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/customer/favorites/${property.id}`, { headers: getAuthHeader() });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/customer/favorites/${property.id}`, {}, { headers: getAuthHeader() });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setReportLoading(true);
    try {
      await axios.post(`${API_URL}/api/reports`, {
        target_id: property.id,
        target_type: 'property',
        ...reportForm
      }, { headers: getAuthHeader() });
      toast.success('Report submitted successfully');
      setShowReportModal(false);
      setReportForm({ reason: 'fraud', description: '' });
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setEnquiryLoading(true);

    try {
      await axios.post(`${API_URL}/api/enquiries/${agency.slug}`, {
        ...enquiryForm,
        property_id: propertyId
      });
      toast.success('Enquiry sent! We will contact you soon.');
      setEnquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setEnquiryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-navy-200 rounded w-32 mb-6" />
            <div className="h-[500px] bg-navy-200 rounded-xl mb-6" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-10 bg-navy-200 rounded w-3/4" />
                <div className="h-6 bg-navy-200 rounded w-1/2" />
                <div className="h-32 bg-navy-200 rounded" />
              </div>
              <div className="h-96 bg-navy-200 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-navy-300" />
          <h2 className="text-xl font-semibold text-navy-900 mb-2">Property Not Found</h2>
          <Link to={`/agency/${agency.slug}/properties`}>
            <Button variant="outline">View All Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
  const images = property.images?.length ? property.images : [null];
  const floorPlans = property.floor_plan_images?.length ? property.floor_plan_images : [];
  const hasFloorPlans = floorPlans.length > 0;
  const videoEmbedUrl = getEmbedUrl(property.video_tour_url);
  const hasVideo = !!property.video_tour_url;
  const has360 = !!property.virtual_tour_url;
  const isApartmentOrVilla = ['apartment', 'villa', 'independent_house'].includes(property.property_type);
  const isLandOrPlot = ['land', 'plot'].includes(property.property_type);
  const isProject = property.property_type === 'project';
  const isVenture = property.property_type === 'venture';

  // Trust badges
  const trustBadges = [];
  if (property.badge_rera_verified) trustBadges.push({ label: 'RERA Verified', color: 'bg-green-100 text-green-700' });
  if (property.badge_bank_approved) trustBadges.push({ label: 'Bank Approved', color: 'bg-blue-100 text-blue-700' });
  if (property.badge_legal_checked) trustBadges.push({ label: 'Legal Checked', color: 'bg-purple-100 text-purple-700' });
  if (property.badge_hmda_approved) trustBadges.push({ label: 'HMDA Approved', color: 'bg-amber-100 text-amber-700' });
  if (property.badge_dtcp_approved) trustBadges.push({ label: 'DTCP Approved', color: 'bg-cyan-100 text-cyan-700' });

  // Media tabs
  const mediaTabs = [{ key: 'photos', label: `Photos (${images.length})` }];
  if (hasFloorPlans) mediaTabs.push({ key: 'floorplans', label: `Floor Plans (${floorPlans.length})` });
  if (has360) mediaTabs.push({ key: '360', label: '360° Tour' });
  if (hasVideo) mediaTabs.push({ key: 'video', label: 'Video Tour' });

  const activeImages = activeMediaTab === 'floorplans' ? floorPlans : images;

  // Consolidate amenities
  const allAmenities = [
    ...(property.amenities || []),
    property.power_backup ? 'Power Backup' : null,
    property.gated_community ? 'Gated Community' : null,
    property.corner_plot ? 'Corner Plot' : null,
    property.water_source === 'municipal' ? 'Municipal Water' : null,
    property.water_source === 'borewell' ? 'Borewell Water' : null
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid="property-detail">
      <Helmet>
        <title>{property ? `${property.title} | ${agency?.company_name || 'EstateCloud'}` : 'Property Details'}</title>
      </Helmet>

      {/* Navbar area placeholder or back button */}
      <div className="border-b border-navy-100 bg-white sticky top-0 z-40 bg-opacity-90 backdrop-blur-md no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to={location.state?.from || `/agency/${agency.slug}/properties`}
            className="flex items-center gap-2 text-navy-600 hover:text-navy-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {location.state?.from === '/search' ? 'Search Results' : 'Listings'}
          </Link>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => window.print()} className="text-navy-600 hover:bg-navy-50">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="ghost" size="sm" className="text-navy-600 hover:bg-navy-50">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Header Section */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-3 max-w-3xl">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="bg-navy-100 text-navy-700 hover:bg-navy-200 capitalize px-3 py-1">
                  {property.property_type.replace('_', ' ')}
                </Badge>
                {property.project_status === 'ready' && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 px-3 py-1">Ready to Move</Badge>}
                {property.is_premium && <Badge className="bg-purple-100 text-purple-800 border-purple-200 px-3 py-1">Premium</Badge>}
                {property.is_verified && (
                  <Badge className="bg-sky-100 text-sky-800 border-sky-200 px-3 py-1 gap-1">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </Badge>
                )}
                {property.is_sponsored && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 px-3 py-1 gap-1">
                    <Star className="w-3 h-3 fill-amber-500" /> Sponsored
                  </Badge>
                )}
                {trustBadges.map(badge => (
                  <Badge key={badge.label} className={`${badge.color} border px-3 py-1`}>{badge.label}</Badge>
                ))}
              </div>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-navy-900 leading-tight tracking-tight">
                {property.title}
              </h1>
              <div className="flex items-center text-navy-500 gap-2 text-lg">
                <MapPin className="w-5 h-5 text-navy-400" />
                <span>
                  {[property.location_area, property.location_city, property.location_state].filter(Boolean).join(', ')}
                  {property.location_pincode && ` - ${property.location_pincode}`}
                </span>
              </div>
            </div>

            <div className="text-right">
              <div className="text-4xl font-bold text-navy-900 tracking-tight">
                {isVenture && property.price_min && property.price_max
                  ? `${formatPrice(property.price_min)} - ${formatPrice(property.price_max)}`
                  : formatPrice(property.price)
                }
              </div>
              {property.price_per_sqft && (
                <p className="text-navy-500 font-medium mt-1">₹{property.price_per_sqft.toLocaleString()} / sqft</p>
              )}
            </div>
          </div>
        </div>

        {/* Hero Image Mosaic */}
        <ImageMosaic images={images} title={property.title} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Left Column: Details */}
          <div className="lg:col-span-2 space-y-12">

            {/* Quick Stats Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 bg-navy-50 rounded-2xl border border-navy-100/50">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm border border-navy-50"><Maximize className="w-6 h-6 text-navy-700" /></div>
                <div><p className="text-xs text-navy-500 font-bold uppercase tracking-wider mb-1">Area</p><p className="font-bold text-navy-900 text-lg">{property.size} <span className="text-sm font-medium text-navy-500">{property.size_unit}</span></p></div>
              </div>
              {property.bedrooms && (
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-xl shadow-sm border border-navy-50"><Bed className="w-6 h-6 text-navy-700" /></div>
                  <div><p className="text-xs text-navy-500 font-bold uppercase tracking-wider mb-1">Bedrooms</p><p className="font-bold text-navy-900 text-lg">{property.bedrooms} BHK</p></div>
                </div>
              )}
              {property.bathrooms && (
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-xl shadow-sm border border-navy-50"><Bath className="w-6 h-6 text-navy-700" /></div>
                  <div><p className="text-xs text-navy-500 font-bold uppercase tracking-wider mb-1">Bathrooms</p><p className="font-bold text-navy-900 text-lg">{property.bathrooms}</p></div>
                </div>
              )}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm border border-navy-50"><Compass className="w-6 h-6 text-navy-700" /></div>
                <div><p className="text-xs text-navy-500 font-bold uppercase tracking-wider mb-1">Facing</p><p className="font-bold text-navy-900 text-lg capitalize">{property.facing_direction?.replace('_', ' ') || 'N/A'}</p></div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-6 border-b border-navy-100 pb-10">
              <h2 className="text-2xl font-bold text-navy-900">About this property</h2>
              <div className="prose prose-navy max-w-none text-navy-600 whitespace-pre-wrap leading-relaxed">
                {property.description}
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-6 border-b border-navy-100 pb-10">
              <h2 className="text-2xl font-bold text-navy-900">Amenities & Features</h2>
              <AmenitiesGrid amenities={allAmenities} />
              {allAmenities.length === 0 && <p className="text-navy-500 italic">No specific amenities listed.</p>}
            </div>

            {/* Property Map */}
            <div className="space-y-6 border-b border-navy-100 pb-10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-navy-900">Location</h2>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`, '_blank')}>
                  <Navigation className="w-4 h-4" /> Get Directions
                </Button>
              </div>

              {property.latitude && property.longitude ? (
                <div className="rounded-2xl overflow-hidden border border-navy-100 shadow-sm">
                  <PropertyMap lat={property.latitude} lng={property.longitude} title={property.title} address={property.full_address} className="h-[400px] w-full" />
                </div>
              ) : (
                <div className="h-64 bg-navy-50 rounded-2xl flex items-center justify-center text-navy-400 border border-navy-100">Map not available</div>
              )}
              {property.full_address && (
                <div className="flex items-start gap-3 p-4 bg-navy-50 rounded-lg text-navy-700">
                  <MapPin className="w-5 h-5 text-navy-500 mt-0.5 flex-shrink-0" />
                  <p>{property.full_address}</p>
                </div>
              )}

              {/* Neighborhood Insights (Schools, Hospitals, etc.) */}
              {(property.latitude && property.longitude) && (
                <div className="mt-8 space-y-6">
                  <div className="flex items-center gap-2">
                    <Compass className="w-6 h-6 text-sky-600" />
                    <h2 className="text-2xl font-bold text-navy-900">Neighborhood Insights</h2>
                  </div>
                  <NeighborhoodInsights lat={property.latitude} lng={property.longitude} />
                </div>
              )}
            </div>

            {/* Venture Units Table (if applicable) */}
            {isVenture && property.units?.length > 0 && (
              <div className="space-y-6 border-b border-navy-100 pb-10">
                <h2 className="text-2xl font-bold text-navy-900">Unit Availability</h2>
                <div className="overflow-hidden border border-navy-200 rounded-xl shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-navy-50/80 backdrop-blur">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold text-navy-900">Unit No.</th>
                        <th className="px-6 py-4 text-left font-semibold text-navy-900">Size</th>
                        <th className="px-6 py-4 text-right font-semibold text-navy-900">Price</th>
                        <th className="px-6 py-4 text-center font-semibold text-navy-900">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-navy-100 bg-white">
                      {property.units.map((unit, i) => (
                        <tr key={i} className="hover:bg-navy-50/50 transition-colors">
                          <td className="px-6 py-4 font-medium text-navy-900">{unit.unit_number}</td>
                          <td className="px-6 py-4 text-navy-600">{unit.size} {unit.size_unit}</td>
                          <td className="px-6 py-4 text-right font-medium text-navy-900">{formatPrice(unit.price)}</td>
                          <td className="px-6 py-4 text-center">
                            <Badge variant={unit.status === 'available' ? 'default' : 'secondary'} className={
                              unit.status === 'available' ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200' :
                                unit.status === 'sold' ? 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' :
                                  'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200'
                            }>
                              {unit.status.charAt(0).toUpperCase() + unit.status.slice(1)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Legal / Other Details Grid */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-navy-900">Additional Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Project Info */}
                <div className="p-6 rounded-2xl border border-navy-100 bg-white space-y-4 shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-navy-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-500" /> Project Info
                  </h3>
                  <div className="space-y-3">
                    <DetailRow icon={Calendar} label="Property Age" value={property.property_age} />
                    <DetailRow icon={Calendar} label="Year Built" value={property.year_built} />
                    <DetailRow icon={Layers} label="Total Floors" value={property.total_floors} />
                    <DetailRow icon={Building2} label="Total Units" value={property.total_units} />
                  </div>
                </div>

                {/* Legal Info */}
                <div className="p-6 rounded-2xl border border-navy-100 bg-white space-y-4 shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-navy-900 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-indigo-500" /> Legal & Approvals
                  </h3>
                  <div className="space-y-3">
                    <DetailRow icon={ShieldCheck} label="RERA ID" value={property.rera_number} />
                    <DetailRow icon={Landmark} label="Approval" value={property.approval_authority} />
                    <DetailRow icon={FileCheck} label="Ownership" value={ownershipLabels[property.ownership_type]} />
                    <DetailRow icon={Landmark} label="Bank Loan" value={property.bank_approved ? 'Available' : 'Pending'} />
                  </div>
                </div>
              </div>

              {/* Documents & Approvals Section */}
              {property.documents?.length > 0 && (
                <div className="mt-8 p-6 rounded-2xl border border-navy-100 bg-navy-50/30 space-y-6">
                  <h3 className="text-xl font-bold text-navy-900 flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6 text-emerald-500" /> Verified Documents & Approvals
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {property.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-white rounded-xl border border-navy-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                            <FileCheck className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-navy-900 text-sm">{doc.name}</p>
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-[10px] mt-1">
                              {doc.status}
                            </Badge>
                          </div>
                        </div>
                        <a
                          href={getImageUrl(doc.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-navy-50 rounded-full transition-colors text-sky-600"
                          title="View Document"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="lg:col-span-1 no-print">
            <div className="sticky top-24 space-y-6">
              <PropertySidebar agency={agency} propertyId={property.id} onEnquire={handleEnquirySubmit} />

              {/* Report Button (Secondary Action) */}
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-navy-400 hover:text-red-500 hover:bg-red-50"
                  onClick={() => setShowReportModal(true)}
                >
                  <Flag className="w-4 h-4 mr-2" /> Report this property
                </Button>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report Property</DialogTitle></DialogHeader>
          <form onSubmit={handleReportSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input" value={reportForm.reason} onChange={e => setReportForm({ ...reportForm, reason: e.target.value })}>
                <option value="fraud">Fraud / Fake Listing</option>
                <option value="incorrect_info">Incorrect Information</option>
                <option value="pricing_issue">Pricing Issue</option>
                <option value="sold_out">Sold Out</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={reportForm.description} onChange={e => setReportForm({ ...reportForm, description: e.target.value })} required placeholder="Please provide details..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
              <Button type="submit" variant="destructive" disabled={reportLoading}>Submit Report</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
